import React, { useState } from 'react';
import { useAtom } from 'jotai';
import PageTitle from '../../components/PageTitle';
import styles from './css/index.module.scss';
import MyInput from '../../components/MyInput';
import FileIcon from '../../assets/formIcons/file.svg';
import {
  POPUP_SCRIPT,
  getButtonMinimalCode,
  getButtonSelectorCode,
  getLinksSectionText, getButtonMaximalCode,
} from '../../constants/integration';
import MyHorizontalSelect from '../../components/MyHorizontalSelect';
import AttributesTable from './components/AttributesTable';
import EmptyHeight from '../../components/EmptyHeight';
import IconButton from '../../components/IconButton';
import sleep from '../../helpers/sleep';
import ClipboardCopiedIcon from '../../assets/icons/clipboard_copied.svg';
import { addressesAtom } from '../../atoms/accountAtoms';
import DropdownInput from '../../components/DropdownInput';

const INTEGRATION_MODES = [
  { id: 'popup', name: 'Поверх текущего сайта во всплывающем окне' },
  { id: 'link', name: 'На отдельной странице (только ссылка)' },
];

function Integration() {
  const [integrationMode, setIntegrationMode] = useState(INTEGRATION_MODES[0]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [copiedBlock, setCopiedBlock] = useState(null);
  const [addresses] = useAtom(addressesAtom);

  const addressOptions = (addresses || []).map((a) => ({ value: a.id, label: a.name }));
  const currentAddress = addresses?.find((a) => a.id === selectedAddressId) || addresses?.[0];

  const copyToClipboard = async (text, blockKey) => {
    setCopiedBlock(blockKey);
    await navigator.clipboard.writeText(text);
    await sleep(1000);
    setCopiedBlock(null);
  };

  const onFileDownloadClick = (ev) => {
    ev.preventDefault();
    const link = document.createElement('a');
    link.href = '/files/reservista_users_agreement.pdf';
    link.download = 'reservista_users_agreement.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderCodeBlock = (title, code, blockKey) => (
    <div key={blockKey} className={styles.codeBlock}>
      <div className={styles.codeBlockHeader}>
        <IconButton
          wrapperClassName={styles.copyButton}
          svgPath={copiedBlock === blockKey ? ClipboardCopiedIcon : FileIcon}
          onClick={() => copyToClipboard(code, blockKey)}
        />
        <span className={styles.codeBlockTitle}>{title}</span>
      </div>
      <MyInput
        value={code}
        onChange={() => {}}
        isLarge
        withLeftIcon={false}
        withCloseIcon={false}
        disabled
        className={styles.integrationInput}
        wrapperClassName={`${styles.codeBlockInputWrapper} ${styles[`codeBlockInputWrapper_${blockKey}`]}`}
      />
    </div>
  );

  return (
    <div className={styles.integration}>
      <PageTitle text="Интеграция формы онлайн бронирования" />
      <div className={styles.integrationContent}>
        <p className={styles.integrationQuestion}>
          Как вы хотите открывать форму бронирования?
        </p>
        <MyHorizontalSelect
          list={INTEGRATION_MODES}
          setSelected={setIntegrationMode}
          selected={integrationMode}
          displayPath="name"
          keyPath="id"
          className={styles.modeSelect}
        />

        {integrationMode.id === 'popup' && (
          <>
            <EmptyHeight height={100} />
            <p className={styles.integrationContentText}>
              Для работы всплывающего окна необходимо подключить на ваш сайт следующий скрипт:
            </p>
            {renderCodeBlock('Скрипт виджета', POPUP_SCRIPT, 'script')}
            <EmptyHeight height={100} />
            <p className={styles.integrationContentText}>
              Есть два варианта добавления кнопки для открытия всплывающего окна (оба варианта можно указывать несколько раз и даже комбинировать между собой на одной странице):
            </p>
            <EmptyHeight height={16} />
            <p className={styles.integrationSubtitle}>
              1. Отдельная кнопка для каждого адреса
            </p>
            <p className={styles.integrationContentTextGray}>
              Выберите адрес для подстановки в код:
            </p>
            {addressOptions.length > 0 && (
              <div className={styles.addressSelectWrapper}>
                <DropdownInput
                  placeholder="Выберите адрес"
                  value={currentAddress ? { value: currentAddress.id, label: currentAddress.name } : null}
                  onChange={(val) => setSelectedAddressId(val?.value ?? null)}
                  data={addressOptions}
                />
              </div>
            )}
            {renderCodeBlock(
              'Код кнопки с минимальными атрибутами',
              getButtonMinimalCode(currentAddress),
              'buttonMinimal',
            )}
            <EmptyHeight height={8} />
            {renderCodeBlock(
              'Код кнопки с максимальным количеством атрибутов',
                getButtonMaximalCode(currentAddress),
              'buttonMaximal',
            )}
            <EmptyHeight height={54} />
            <p className={styles.integrationSubtitle}>
              2. Кнопка с выбором ресторана
            </p>
            <p className={styles.integrationContentTextGray}>
              По нажатию на кнопку можно выбрать адрес и открыть форму бронирования для выбранного ресторана.
            </p>
            {renderCodeBlock(
              'Кнопка позволяющая выбрать ресторан для открытия всплывающей формы бронирования',
              getButtonSelectorCode(addresses),
              'buttonSelector',
            )}
            <EmptyHeight height={24} />
            <p className={styles.integrationContentText}>
              Важными атрибутами являются <code>class</code> и <code>data-restaurant-id</code>, остальные
              атрибуты, описанные ниже, необязательны. Кнопки можно кастомизировать любым способом (в том числе
              добавлять другие классы), не изменяя эти два атрибута.
            </p>
            <EmptyHeight height={24} />
            <p className={styles.integrationSubtitle}>Описание HTML-атрибутов кнопки</p>
            <div className={styles.attributesBlock}>
              <AttributesTable />
            </div>
          </>
        )}

        {integrationMode.id === 'link' && (
          <>
            <EmptyHeight height={100} />
            <p className={styles.integrationContentText}>
              Ссылки для открытия формы бронирования отдельно от сайта.
            </p>
            {addressOptions.length > 0 && (
              <p className={styles.integrationContentTextGray}>
                Выберите адрес для подстановки в ссылки:
              </p>
            )}
            {addressOptions.length > 0 && (
              <div className={styles.addressSelectWrapper}>
                <DropdownInput
                  placeholder="Выберите адрес"
                  value={currentAddress ? { value: currentAddress.id, label: currentAddress.name } : null}
                  onChange={(val) => setSelectedAddressId(val?.value ?? null)}
                  data={addressOptions}
                />
              </div>
            )}
            {renderCodeBlock(
              'Код со ссылками',
              getLinksSectionText(currentAddress),
              'links',
            )}
          </>
        )}

        <EmptyHeight height={40} />
        <a href="#" onClick={onFileDownloadClick}>
          Инструкция пользователя по работе с приложением Reservista
        </a>
        <EmptyHeight height={100} />
      </div>
    </div>
  );
}

export default Integration;
